import { makeChoices } from "../../distractors";
import { numUnit, text } from "../../format";
import type { QuestionDraft, Rng, TopicDef } from "../../types";
import { both, numeric, q } from "../helpers";

const CM2 = { pl: "cm²", en: "cm²" };
const CM = { pl: "cm", en: "cm" };
const cm2 = (n: number) => numUnit(n, "cm²");
const cm = (n: number) => numUnit(n, "cm");

function tier1(rng: Rng): QuestionDraft {
  const a = rng.int(2, 12);
  const b = rng.int(2, 12);
  const area = a * b;
  const choice4 = makeChoices(rng, area, [2 * (a + b), a + b, area + a, area - b], (r) => r.int(4, 150), cm2);
  return q(
    both(
      `Prostokąt ma boki ${a} cm i ${b} cm. Jakie jest jego pole?`,
      `A rectangle has sides ${a} cm and ${b} cm. What is its area?`,
    ),
    numeric(area, CM2),
    [`Pole prostokąta = a · b.`, `${a} · ${b} = ${area} cm².`],
    [`Area of a rectangle = a × b.`, `${a} × ${b} = ${area} cm².`],
    { choice4, visual: { kind: "shape", data: { type: "rect", w: a, h: b } } },
  );
}

function tier2(rng: Rng): QuestionDraft {
  const W = rng.int(6, 14);
  const H = rng.int(5, 12);
  const w = rng.int(2, W - 2);
  const h = rng.int(2, H - 2);
  const area = W * H - w * h;
  const choice4 = makeChoices(
    rng,
    area,
    [W * H, W * H + w * h, W * H - w - h, area + w],
    (r) => r.int(10, 200),
    cm2,
  );
  return q(
    both(
      `Z prostokąta ${W} cm × ${H} cm wycięto w rogu prostokąt ${w} cm × ${h} cm. Jakie jest pole powstałej figury (litery L)?`,
      `A ${w} cm × ${h} cm rectangle is cut from the corner of a ${W} cm × ${H} cm rectangle. What is the area of the L-shape left?`,
    ),
    numeric(area, CM2),
    [
      `Pole dużego prostokąta: ${W} · ${H} = ${W * H} cm².`,
      `Pole wyciętego: ${w} · ${h} = ${w * h} cm².`,
      `${W * H} − ${w * h} = ${area} cm².`,
    ],
    [
      `Big rectangle: ${W} × ${H} = ${W * H} cm².`,
      `Cut-out: ${w} × ${h} = ${w * h} cm².`,
      `${W * H} − ${w * h} = ${area} cm².`,
    ],
    { choice4, visual: { kind: "shape", data: { type: "lshape", W, H, w, h } } },
  );
}

function tier3(rng: Rng): QuestionDraft {
  if (rng.chance(0.5)) {
    const W = rng.int(6, 14);
    const H = rng.int(5, 12);
    const w = rng.int(2, W - 2);
    const h = rng.int(2, H - 2);
    const perimeter = 2 * (W + H);
    const choice4 = makeChoices(
      rng,
      perimeter,
      [perimeter - 2 * w, perimeter - w - h, W * H - w * h, perimeter + 2],
      (r) => r.int(10, 80),
      cm,
    );
    return q(
      both(
        `Z prostokąta ${W} cm × ${H} cm wycięto w rogu prostokąt ${w} cm × ${h} cm. Jaki jest obwód powstałej figury (litery L)?`,
        `A ${w} cm × ${h} cm rectangle is cut from the corner of a ${W} cm × ${H} cm rectangle. What is the perimeter of the L-shape left?`,
      ),
      numeric(perimeter, CM),
      [
        `Wycięcie „przesuwa” dwa boki do środka, ale ich długość się nie zmienia.`,
        `Obwód litery L jest taki sam jak obwód całego prostokąta: 2 · (${W} + ${H}) = ${perimeter} cm.`,
      ],
      [
        `The cut-out moves two sides inwards but does not change their total length.`,
        `The perimeter of the L-shape equals the perimeter of the full rectangle: 2 × (${W} + ${H}) = ${perimeter} cm.`,
      ],
      { choice4, visual: { kind: "shape", data: { type: "lshape", W, H, w, h } } },
    );
  }
  const W = rng.int(8, 16);
  const H = rng.int(6, 14);
  const s = rng.int(2, Math.min(W, H) - 3);
  const area = W * H - s * s;
  const choice4 = makeChoices(
    rng,
    area,
    [W * H, W * H - s, W * H - 4 * s, area + s],
    (r) => r.int(20, 250),
    cm2,
  );
  return q(
    both(
      `Ramka ma kształt prostokąta ${W} cm × ${H} cm z kwadratowym otworem o boku ${s} cm. Jakie jest pole ramki?`,
      `A frame is a ${W} cm × ${H} cm rectangle with a square hole of side ${s} cm. What is the area of the frame?`,
    ),
    numeric(area, CM2),
    [
      `Pole prostokąta: ${W} · ${H} = ${W * H} cm².`,
      `Pole otworu: ${s} · ${s} = ${s * s} cm².`,
      `${W * H} − ${s * s} = ${area} cm².`,
    ],
    [
      `Rectangle: ${W} × ${H} = ${W * H} cm².`,
      `Hole: ${s} × ${s} = ${s * s} cm².`,
      `${W * H} − ${s * s} = ${area} cm².`,
    ],
    { choice4, visual: { kind: "shape", data: { type: "frame", W, H, s } } },
  );
}

export const compositeArea: TopicDef = {
  id: "sp6.composite_area",
  level: "sp6",
  name: text("Pola figur złożonych", "Area of composite shapes"),
  modes: ["numeric", "choice4"],
  generate: (rng, tier) => (tier === 1 ? tier1(rng) : tier === 2 ? tier2(rng) : tier3(rng)),
};
